import {
  collection,
  getDocs,
  doc,
  query,
  where,
  orderBy,
  Timestamp,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { FollowUpEntry } from './CalendarTypes';

export class FollowUpService {
  static async fetchFollowUpEntries(
    companyId: string,
    selectedDate: string,
    selectedUserId: string
  ): Promise<FollowUpEntry[]> {
    if (!companyId || !selectedDate || !selectedUserId) return [];

    try {
      const followUpsRef = collection(db, `companies/${companyId}/followUps`);
      const selectedDateStart = new Date(selectedDate);
      const selectedDateEnd = new Date(selectedDate);
      selectedDateEnd.setHours(23, 59, 59, 999);
      
      // Filter followups by selected user and date range (both done and pending)
      const followUpsQuery = query(
        followUpsRef,
        where('userId', '==', selectedUserId),
        where('followUpTime', '>=', Timestamp.fromDate(selectedDateStart)),
        where('followUpTime', '<=', Timestamp.fromDate(selectedDateEnd)),
        orderBy('followUpTime', 'asc')
      );

      const snapshot = await getDocs(followUpsQuery);
      const followUpsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        entryType: 'followup'
      })) as FollowUpEntry[];
      
      // Fetch lead details for each followup
      const enhancedFollowUps = await Promise.all(followUpsData.map(async (followup) => {
        try {
          // Get lead name using leadId
          if (followup.leadId) {
            const leadRef = doc(db, `companies/${companyId}/leads`, followup.leadId);
            const leadDoc = await getDoc(leadRef);
            if (leadDoc.exists()) {
              const leadData = leadDoc.data();
              followup.studentName = leadData.name || followup.studentName || 'Unknown';
            }
          }
          
          // Get user name using userId
          if (followup.userId) {
            const userRef = doc(db, 'users', followup.userId);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              followup.assignedUserName = userData.name || userData.email || followup.assignedUserName || 'Unknown';
            }
          }
          
          return followup;
        } catch (error) {
          console.error('Error fetching details for followup:', error);
          return followup;
        }
      }));

      return enhancedFollowUps;
    } catch (error) {
      console.error('Error fetching followup entries:', error);
      return [];
    }
  }

  static async markFollowUpAsDone(
    companyId: string,
    followUpId: string,
    leadId: string
  ): Promise<void> {
    try {
      // Update the followup to mark as done
      const followUpRef = doc(db, `companies/${companyId}/followUps`, followUpId);
      await updateDoc(followUpRef, {
        isDone: true,
        completedAt: Timestamp.now()
      });

      // Update the lead to remove active followup
      if (leadId) {
        const leadRef = doc(db, `companies/${companyId}/leads`, leadId);
        await updateDoc(leadRef, {
          hasActiveFollowUp: false,
          activeFollowUpId: null
        });
      }
    } catch (error) {
      console.error('Error marking followup as done:', error);
      throw error;
    }
  }
}
